import { Injectable, inject } from '@angular/core';
import { HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod } from '@api-new/authservice';
import { ENUM_MortgageRepaymentType } from '@api-new/common';
import { MortgageApiService } from '@api-new/mortgageservice';
import { ENTRY_FLOW } from '@app/sign-up/constants/entry-flow';
import { MORTGAGE_MONITOR_FLOW } from '@app/sign-up/constants/mortgage-monitor-flow';
import { REMORTGAGE_NOW_FLOW } from '@app/sign-up/constants/remotgage-now-flow';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MAX_INITIAL_INTEREST_RATE_NEW } from '@shared-lib/constants/mortgage';
import { MOVING_HOME_FLOW } from '@shared-lib/constants/sign-up/moving-home-flow';
import { AppState } from '@shared/models/app-state.model';
import { LocalStorageSyncService } from '@shared/services/local-storage-sync.service';
import { setSignUpFlowCurrentStep } from '@shared/store/sign-up/flow/flow.actions';
import { selectSignUpFlowData } from '@shared/store/sign-up/flow/flow.selectors';
import {
  clearSignUpMortgageFromStorage,
  loadSignUpMortgageFromStorage,
  loadSignUpMortgageFromStorageSuccess,
  saveSignUpMortgageToStorage,
  setSignUpMortgageRateByPayments,
  skipSignUpMortgageCreation,
} from '@shared/store/sign-up/mortgage/mortgage.actions';
import { selectSignUpMortgageState } from '@shared/store/sign-up/mortgage/mortgage.selectors';
import { signUpMortgageSelectorName } from '@shared/store/sign-up/mortgage/mortgage.state';
import { EMPTY, tap, withLatestFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class SignUpMortgageEffects {
  private readonly actions$ = inject(Actions);
  private readonly store: Store<AppState> = inject(Store<AppState>);
  private readonly localStorageSyncService = inject(LocalStorageSyncService);
  private readonly mortgageApiService = inject(MortgageApiService);

  loadMortgageFromStorage = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSignUpMortgageFromStorage),
      map(() => loadSignUpMortgageFromStorageSuccess({ state: this.localStorageSyncService.loadFromStorage(signUpMortgageSelectorName) })),
    ),
  );

  saveMortgageToStorage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveSignUpMortgageToStorage),
        withLatestFrom(this.store.select(selectSignUpMortgageState)),
        tap(([_, mortgageState]) => {
          this.localStorageSyncService.saveToStorage(signUpMortgageSelectorName, mortgageState);
        }),
      ),
    { dispatch: false },
  );

  clearSignUpMortgageFromStorage = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clearSignUpMortgageFromStorage),
        tap(() => {
          this.localStorageSyncService.deleteFromStorage(signUpMortgageSelectorName);
        }),
      ),
    { dispatch: false },
  );

  skipMortgageCreation = createEffect(() =>
    this.actions$.pipe(
      ofType(skipSignUpMortgageCreation),
      withLatestFrom(this.store.select(selectSignUpFlowData)),
      map(([_, flowData]) => {
        switch (flowData.creationMethod) {
          case HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod.SIGN_UP_CREATION_METHOD_MORTGAGE_MONITOR:
            return setSignUpFlowCurrentStep({ step: MORTGAGE_MONITOR_FLOW.UserCreate });
          case HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod.SIGN_UP_CREATION_METHOD_MOVING_HOME:
            return setSignUpFlowCurrentStep({ step: MOVING_HOME_FLOW.UserCreate });
          case HTTP_CP_SignUp_Request_ENUM_SignUpCreationMethod.SIGN_UP_CREATION_METHOD_REMORTGAGE_NOW:
            return setSignUpFlowCurrentStep({ step: REMORTGAGE_NOW_FLOW.SmartSearchLiveResults });
        }

        return setSignUpFlowCurrentStep({ step: ENTRY_FLOW.Landing });
      }),
    ),
  );

  setSignUpMortgageRateByPayments = createEffect(() =>
    this.actions$.pipe(
      ofType(setSignUpMortgageRateByPayments),
      withLatestFrom(this.store.select(selectSignUpMortgageState)),
      switchMap(([{ monthlyPayment }, mortgageState]) => {
        const mortgageData = mortgageState.entity;

        if (mortgageData == null || monthlyPayment == null) {
          return EMPTY;
        }

        const firstMortgagePart = mortgageData.mortgageParts.parts[0];

        return (
          firstMortgagePart.repaymentType === ENUM_MortgageRepaymentType.MORTGAGE_REPAYMENT_TYPE_FULL_REPAYMENT
            ? this.mortgageApiService.HTTP_X_CalculateInterestRate_ForFullRepayment({
                monthlyPayment: monthlyPayment,
                termInMonths: firstMortgagePart.term,
                loanAmount: firstMortgagePart.borrowedAmount,
              })
            : this.mortgageApiService.HTTP_X_CalculateInterestRate_ForInterestOnlyRepayment({
                monthlyPayment: monthlyPayment,
                loanAmount: firstMortgagePart.borrowedAmount,
              })
        ).pipe(
          map(({ interestRate }) => {
            return saveSignUpMortgageToStorage({
              ...mortgageData,
              monthlyPayment: { monthlyPayment, rate: Math.min(interestRate * 100, MAX_INITIAL_INTEREST_RATE_NEW) },
            });
          }),
        );
      }),
    ),
  );
}
